import React, { useEffect, useState } from 'react';
import '../App.css';
import { v4 as uuidv4 } from 'uuid';
import Button from '@mui/material/Button';
import GameComponent from '../components/GameComponent';
import {Container, CssBaseline, Divider, styled, Typography, Box, Alert, CircularProgress } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import configureAmplify from '../configuration/amplify';
import { Amplify } from 'aws-amplify';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, LocalizationProvider, PickersDay, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { on } from 'events';
import GameSelector from '../components/GameSelector';
import Grid2 from '@mui/material/Unstable_Grid2';
import { fetchAuthSession } from 'aws-amplify/auth';
Amplify.configure(configureAmplify());

type Game = {
    gameid?: string;
    firstname?: string;
    middlename?: string;
    lastname?: string;
    questions?: Question[];
    gamedate: string;
}

type Question = {
    id: number;
    question: string;
    answer: string;
}

type ScheduleEntry = {
    GameDate: string;
    GameId: string;
    gameName: string;
    lastUpdated?: string;
}

const ManageSchedule: React.FC = () => {
    const [gameList, setGameList] = useState([] as Game[]);
    const [selectedDay, setSelectedDay] = useState(dayjs());
    const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    
    // Create a map of dates to game names for the calendar
    const scheduleMap = new Map<string, ScheduleEntry>();
    schedule.forEach(entry => {
        scheduleMap.set(entry.GameDate, entry);
    });

    const loadGames = async () => {
        const session = await fetchAuthSession();
        const requestOptions = {
            method: 'GET',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.tokens?.idToken}`
             }
        };
        fetch('https://api.admin.famousinitials.com/games', requestOptions)
        .then(async response => {
            const data = await response.json();
            console.log(data);
            
            // check for error response
            if (!response.ok) {
                // get error message from body or default to response status
                const error = (data && data.message) || response.status;
                return Promise.reject(error);
            }
            data.sort((a: Game, b: Game) => {
                if(!a.gamedate) {
                    return 1;
                }
                if(!b.gamedate) {
                    return -1;
                }
                return new Date(a.gamedate).getTime() - new Date(b.gamedate).getTime()})
            sortGames();
            setGameList(data);
        })
        .catch(error => {
            console.error('There was an error!', error);
        })
    };

    const loadSchedule = async () => {
        try {
            setLoading(true);
            setError('');
            const session = await fetchAuthSession();
            const requestOptions = {
                method: 'GET',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.tokens?.idToken}`
                }
            };
            
            const response = await fetch('https://api.admin.famousinitials.com/gameschedule', requestOptions);
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Failed to load schedule');
            }
            
            console.log('Loaded schedule:', data);
            setSchedule(data);
        } catch (err) {
            console.error('Error loading schedule:', err);
            setError(err instanceof Error ? err.message : 'Failed to load schedule');
        } finally {
            setLoading(false);
        }
    };

    const sortGames = () => {
        gameList.sort((a: Game, b: Game) => {
                if(!a.gamedate) {
                    return 1;
                }
                if(!b.gamedate) {
                    return -1;
                }
                return new Date(a.gamedate).getTime() - new Date(b.gamedate).getTime()})
        setGameList(gameList);
    }

    const deleteGame = (id: string) => {
        const requestOptions = {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' }
        };
        fetch(`https://api.admin.famousinitials.com/games/${id}`, requestOptions)
        .then(async response => {
            const data = await response.json();
            console.log(data);
            
            // check for error response
            if (!response.ok) {
                // get error message from body or default to response status
                const error = (data && data.message) || response.status;
                return Promise.reject(error);
            }

            setGameList(gameList.filter(game => game.gameid !== id));
        })
        .catch(error => {
            console.error('There was an error!', error);
        })
    };

    useEffect(() => {
        loadGames();
        loadSchedule();
    }, []);

    const HighlightedDay = styled(PickersDay)(({ theme }) => ({
    "&.Mui-selected": {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
    }));

    const onDaySelect = (day: Dayjs) => {
        setSelectedDay(day);
    };

    const ServerDay = (props: any) => {
        const { day, outsideCurrentMonth, ...other } = props;
        const dateStr = day.format("YYYY-MM-DD");
        const scheduleEntry = scheduleMap.get(dateStr);
        const isScheduled = !!scheduleEntry;

        return (
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <PickersDay
                    {...other}
                    outsideCurrentMonth={outsideCurrentMonth}
                    day={day}
                    onClick={() => onDaySelect(day)}
                    sx={{
                        backgroundColor: isScheduled ? 'primary.light' : 'inherit',
                        '&:hover': {
                            backgroundColor: isScheduled ? 'primary.main' : 'action.hover',
                        },
                        '&.Mui-selected': {
                            backgroundColor: 'primary.dark',
                        }
                    }}
                />
                {isScheduled && (
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            fontSize: '0.6rem', 
                            textAlign: 'center', 
                            maxWidth: '40px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            mt: -0.5
                        }}
                        title={scheduleEntry.gameName}
                    >
                        {scheduleEntry.gameName}
                    </Typography>
                )}
            </Box>
        );
    };

    const handleScheduleUpdate = () => {
        loadSchedule(); // Refresh the schedule when a game is scheduled
    };
    
    return (
        <React.Fragment>
            <CssBaseline />
            <Container maxWidth='xl'>
                <Box sx={{ py: 3 }}>
                    <Typography variant="h4" gutterBottom>
                        Manage Game Schedule
                    </Typography>
                    
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    
                    <Grid2 container spacing={3}>
                        <Grid2>
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Calendar
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Click a date to schedule a game. Highlighted dates show scheduled games.
                                </Typography>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DateCalendar
                                        value={selectedDay}
                                        onChange={onDaySelect}
                                        minDate={dayjs()}
                                        slots={{
                                            day: ServerDay,
                                        }}
                                    />
                                </LocalizationProvider>
                                {selectedDay && (
                                    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                        <Typography variant="subtitle1">
                                            Selected: {selectedDay.format('MMMM D, YYYY')}
                                        </Typography>
                                        {scheduleMap.get(selectedDay.format('YYYY-MM-DD')) && (
                                            <Typography variant="body2" color="primary">
                                                Scheduled: {scheduleMap.get(selectedDay.format('YYYY-MM-DD'))?.gameName}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Grid2>
                        <Grid2>
                            <GameSelector 
                                selectedDate={selectedDay?.format('YYYY-MM-DD')}
                                onScheduleUpdate={handleScheduleUpdate}
                            />
                        </Grid2> 
                    </Grid2>
                </Box>
            </Container>
        </React.Fragment>
    );
}

function createNewGame(): Game {
    return { gameid: uuidv4(), firstname: '', middlename: '', lastname: '', gamedate: ''};
}

export default ManageSchedule;
